const SAFE_FALLBACK = "/dashboard";

const safeDecodeURIComponent = (str, maxIterations = 1) => {
  if (!str || typeof str !== "string") return null;
  
  let current = str;
  let iterations = 0;
  
  try {
    while (iterations < maxIterations) {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
      iterations++;
    }
    
    if (iterations > 0 && current.includes("%")) {
      try {
        const doubleDecoded = decodeURIComponent(current);
        if (doubleDecoded !== current) {
          return null;
        }
      } catch {
      }
    }
    
    return current;
  } catch {
    return null;
  }
};

const normalizeUnicode = (str) => {
  if (!str) return "";
  
  const unicodeLookalikes = {
    '\uff0e': '.',
    '\u2024': '.',
    '\ufe52': '.',
    '\u2215': '/',
    '\uff0f': '/',
    '\u29f8': '/',
    '\u2044': '/',
    '\uff3c': '\\',
    '\u2216': '\\',
    '\ufe68': '\\',
  };
  
  let normalized = str;
  for (const [unicode, ascii] of Object.entries(unicodeLookalikes)) {
    normalized = normalized.split(unicode).join(ascii);
  }
  
  return normalized;
};

const getPathPortion = (str) => {
  if (!str) return "";
  const queryIndex = str.indexOf("?");
  return queryIndex === -1 ? str : str.slice(0, queryIndex);
};

const getQueryPortion = (str) => {
  if (!str) return "";
  const queryIndex = str.indexOf("?");
  return queryIndex === -1 ? "" : str.slice(queryIndex);
};

const containsDangerousPathPatterns = (pathOnly, rawPath) => {
  if (!pathOnly) return false;
  
  const lowerPath = pathOnly.toLowerCase();
  const lowerRaw = rawPath?.toLowerCase() || "";
  const normalized = normalizeUnicode(lowerPath);
  
  const traversalRegex = /(^|\/)\.\.(\/$|\/|$)/;
  if (traversalRegex.test(lowerPath) || traversalRegex.test(normalized)) {
    return true;
  }
  
  const encodedTraversalPatterns = ["%2e%2e", "%2e.", ".%2e", "%252e", "..;"];
  for (const pattern of encodedTraversalPatterns) {
    if (lowerRaw.includes(pattern)) {
      return true;
    }
  }
  
  const dangerousPathPatterns = ["://", "javascript:", "data:", "vbscript:", "file:", "blob:", "about:", "\\"];
  
  for (const pattern of dangerousPathPatterns) {
    if (lowerPath.includes(pattern) || 
        lowerRaw.includes(pattern) || 
        normalized.includes(pattern)) {
      return true;
    }
  }
  
  if (/[\x00-\x1f\x7f]/.test(pathOnly)) {
    return true;
  }
  
  const encodedControlChars = ["%0d", "%0a", "%00", "%09", "%0b", "%0c"];
  for (const pattern of encodedControlChars) {
    if (lowerRaw.includes(pattern)) {
      return true;
    }
  }
  
  const afterFirst = lowerPath.startsWith("/") ? lowerPath.slice(1) : lowerPath;
  if (afterFirst.includes("//")) {
    return true;
  }
  
  const encodedSlashPatterns = ["%2f%2f", "%2f/", "/%2f", "%252f"];
  for (const pattern of encodedSlashPatterns) {
    if (lowerRaw.includes(pattern)) {
      return true;
    }
  }
  
  return false;
};

const containsDangerousQueryPatterns = (queryOnly) => {
  if (!queryOnly) return false;
  
  if (/[\x00-\x1f\x7f]/.test(queryOnly)) {
    return true;
  }
  
  const xssPatterns = ["<", ">", "\"", "'", "\\"];
  
  for (const pattern of xssPatterns) {
    if (queryOnly.includes(pattern)) {
      return true;
    }
  }
  
  const lowerQuery = queryOnly.toLowerCase();
  const encodedControlChars = ["%0d", "%0a", "%00"];
  for (const pattern of encodedControlChars) {
    if (lowerQuery.includes(pattern)) {
      return true;
    }
  }
  
  return false;
};

const isValidPathPattern = (pathOnly) => {
  if (!pathOnly) return false;
  
  const strictPathRegex = /^\/dashboard\/?([a-zA-Z0-9_\-]+)?(\/[a-zA-Z0-9_\-]+)*\/?$/;
  if (strictPathRegex.test(pathOnly)) {
    return true;
  }
  
  const relaxedPathRegex = /^\/dashboard\/?([a-zA-Z0-9_\-.:]+)?(\/[a-zA-Z0-9_\-.:]+)*\/?$/;
  if (relaxedPathRegex.test(pathOnly)) {
    if (!/[;|&$`!{}[\]()@]/.test(pathOnly)) {
      return true;
    }
  }
  
  return false;
};

const isValidQueryPattern = (queryOnly) => {
  if (!queryOnly) return true;
  
  if (!queryOnly.startsWith("?")) return false;
  
  return true;
};

export const sanitizePath = (path) => {
  if (!path || typeof path !== "string") return SAFE_FALLBACK;

  const trimmed = path.trim();
  
  if (!trimmed) return SAFE_FALLBACK;
  
  if (trimmed.length > 2048) return SAFE_FALLBACK;
  
  if (trimmed.startsWith("//")) return SAFE_FALLBACK;
  
  if (!trimmed.startsWith("/dashboard")) return SAFE_FALLBACK;
  
  const decoded = safeDecodeURIComponent(trimmed);
  
  if (decoded === null) return SAFE_FALLBACK;
  
  const normalized = normalizeUnicode(decoded);
  
  if (!normalized.startsWith("/dashboard")) return SAFE_FALLBACK;
  
  const pathOnly = getPathPortion(normalized);
  const queryOnly = getQueryPortion(normalized);
  const rawPathOnly = getPathPortion(trimmed);
  
  if (containsDangerousPathPatterns(pathOnly, rawPathOnly)) return SAFE_FALLBACK;
  
  if (containsDangerousQueryPatterns(queryOnly)) return SAFE_FALLBACK;
  
  if (!isValidPathPattern(pathOnly)) return SAFE_FALLBACK;
  
  if (!isValidQueryPattern(queryOnly)) return SAFE_FALLBACK;

  return trimmed;
};

export const getSafeRedirect = (candidate) => {
  if (!candidate) return SAFE_FALLBACK;

  if (typeof candidate === "string") {
    return sanitizePath(candidate);
  }

  const pathname = candidate?.pathname;
  const search = candidate?.search || "";
  
  if (typeof pathname !== "string") return SAFE_FALLBACK;

  return sanitizePath(`${pathname}${search}`);
};
